import Colors from '@constants/Colors';
import { useDynamicStyles, type WithResponsive } from '@sunrise-ui/primitives';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

type Props = {
  communityId?: string;
};

// Communiful brand colors for gradient combinations
const GRADIENT_PALETTES = [
  // Purple to Pink
  [Colors.dark.purple1, Colors.dark.pink0, Colors.dark.purple0],
  // Purple to Cyan (similar to screenshot)
  ['#6B5B95', '#45B7D1', '#96CEB4'],
  // Deep purple variations
  [Colors.dark.purple0, Colors.dark.purple1, '#4A2070'],
  // Pink to Purple
  [Colors.dark.pink0, Colors.dark.purple1, Colors.dark.purple0],
  // Teal to Purple (like the screenshot)
  ['#2D5A5A', '#45B7D1', '#9B59B6'],
  // Sunset purple
  ['#667eea', '#764ba2', Colors.dark.purple0],
];

// Generate a deterministic but seemingly random gradient based on community ID
const getGradientForCommunity = (communityId?: string): string[] => {
  if (!communityId) {
    return GRADIENT_PALETTES[0];
  }

  // Simple hash function to get a consistent index
  let hash = 0;
  for (let i = 0; i < communityId.length; i++) {
    const char = communityId.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  const index = Math.abs(hash) % GRADIENT_PALETTES.length;
  return GRADIENT_PALETTES[index];
};

export default function CommunityGradientBanner({ communityId }: Props) {
  const styles = useDynamicStyles(createStyles);

  const gradientColors = useMemo(
    () => getGradientForCommunity(communityId),
    [communityId],
  );

  return (
    <View style={styles.bannerContainer}>
      <LinearGradient
        colors={gradientColors as [string, string, ...string[]]}
        style={styles.gradient}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
      />
      {/* Overlay shapes to create the angular design like in the screenshot */}
      <View style={styles.overlayContainer}>
        <LinearGradient
          colors={[gradientColors[1], gradientColors[2] ?? gradientColors[0]]}
          style={styles.diagonalShape1}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
        />
        <LinearGradient
          colors={[gradientColors[0], gradientColors[1]]}
          style={styles.diagonalShape2}
          start={{ x: 1, y: 0 }}
          end={{ x: 0, y: 1 }}
        />
      </View>
    </View>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    bannerContainer: {
      width: '100%',
      height: isMobile ? 120 : 180,
      position: 'relative',
      overflow: 'hidden',
      borderRadius: isMobile ? 0 : 12,
    },
    gradient: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
    overlayContainer: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
    diagonalShape1: {
      position: 'absolute',
      top: '-20%',
      right: '-10%',
      width: '60%',
      height: '140%',
      transform: [{ rotate: '-15deg' }],
      opacity: 0.8,
    },
    diagonalShape2: {
      position: 'absolute',
      top: '-30%',
      right: '20%',
      width: '40%',
      height: '160%',
      transform: [{ rotate: '25deg' }],
      opacity: 0.6,
    },
  });
